/**
 * EmailSuggestInput — email input with a floating suggestion list drawn
 * from two sources:
 *
 *   1. Already-in-workforce users (useUsers) — active + pending.
 *   2. WorkOS org roster (useWorkosRoster) — org members + outstanding
 *      pending invitations.
 *
 * Merged by lowercase email (bound wins over workos-member wins over
 * workos-invite), each row rendered with a small source-badge so the
 * admin knows what they're picking. Rows for users already in the
 * current project are disabled (nothing gained by re-adding them).
 *
 * Positioning: the containing DrawerShell body has `overflowY: auto`,
 * which clips any `position: absolute` popover that overflows. We use
 * `position: fixed` with coords measured from the input's
 * `getBoundingClientRect`, updated on open + on resize/scroll, so the
 * list floats above the drawer boundary correctly.
 *
 * Drop-in replacement for `LabeledInput` on the email field — the
 * common props (`label`, `value`, `onChange`, `placeholder`, `required`)
 * are identical; the three new ones supply the data sources.
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import type { UserRow } from '@/hooks/useUsers';
import type { WorkosRosterState } from '@/hooks/useWorkosRoster';
import type { AppRole } from '@/generated/minetwin/types';

type InviteState = 'pending' | 'accepted' | 'expired' | 'revoked';

type Suggestion = {
  /** Original-case email — this is what fills the input on select. */
  email: string;
  /** Lowercased email — the dedupe/filter key. */
  key: string;
  displayName: string;
  source: 'bound' | 'workos-member' | 'workos-invite';
  alreadyInProject: boolean;
  appRole: AppRole | null;
  inviteState?: InviteState;
};

type EmailSuggestInputProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  users: UserRow[];
  workosRoster: WorkosRosterState;
  currentProjectId: string;
};

/** Max suggestion rows visible at once — keeps the list compact and
 *  prevents runaway scroll for orgs with hundreds of WorkOS members. */
const MAX_ROWS = 8;

export function EmailSuggestInput({
  label,
  value,
  onChange,
  placeholder,
  required,
  users,
  workosRoster,
  currentProjectId,
}: EmailSuggestInputProps) {
  const wrapperRef = useRef<HTMLLabelElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  // Merged, deduped suggestion universe. Recomputed only when a source
  // changes — the filter below is what re-runs per keystroke.
  const universe = useMemo<Suggestion[]>(() => {
    const m = new Map<string, Suggestion>();
    // Pass 1: already-in-workforce (highest priority for displayName + role).
    for (const u of users) {
      const key = u.email.toLowerCase();
      m.set(key, {
        email: u.email,
        key,
        displayName: u.displayName || u.email,
        source: 'bound',
        alreadyInProject: currentProjectId
          ? u.projectIds.includes(currentProjectId)
          : false,
        appRole: u.appRole,
      });
    }
    // Pass 2: WorkOS members not already keyed. memberEmails is already lc.
    for (const emailLc of workosRoster.memberEmails) {
      if (m.has(emailLc)) continue;
      m.set(emailLc, {
        email: emailLc,
        key: emailLc,
        displayName: emailLc,
        source: 'workos-member',
        alreadyInProject: false,
        appRole: null,
      });
    }
    // Pass 3: outstanding WorkOS invitations not already keyed.
    for (const [emailLc, inv] of workosRoster.pendingInvitesByEmail) {
      if (m.has(emailLc)) continue;
      m.set(emailLc, {
        email: inv.email,
        key: emailLc,
        displayName: inv.email,
        source: 'workos-invite',
        alreadyInProject: false,
        appRole: null,
        inviteState: inv.state,
      });
    }
    return Array.from(m.values());
  }, [users, workosRoster.memberEmails, workosRoster.pendingInvitesByEmail, currentProjectId]);

  // Filtered on every keystroke. Substring match against displayName OR
  // email. Sort: prefix-matches first (email or name), then substring.
  // Trim + lowercase the query once per pass.
  const matches = useMemo<Suggestion[]>(() => {
    const q = value.trim().toLowerCase();
    if (!q) return [];
    const scored: { s: Suggestion; score: number }[] = [];
    for (const s of universe) {
      const nameLc = s.displayName.toLowerCase();
      const emailLc = s.key;
      // Priority: 0 = email starts-with, 1 = name starts-with, 2 = email substring, 3 = name substring.
      let score = -1;
      if (emailLc.startsWith(q)) score = 0;
      else if (nameLc.startsWith(q)) score = 1;
      else if (emailLc.includes(q)) score = 2;
      else if (nameLc.includes(q)) score = 3;
      if (score === -1) continue;
      scored.push({ s, score });
    }
    scored.sort((a, b) => a.score - b.score || a.s.key.localeCompare(b.s.key));
    return scored.slice(0, MAX_ROWS).map((r) => r.s);
  }, [universe, value]);

  // Reset the highlight when the visible list changes shape. Skip the
  // first "already-in-project" rows so Enter never activates a disabled
  // row by accident.
  useEffect(() => {
    setActiveIdx(matches.findIndex((s) => !s.alreadyInProject));
  }, [matches]);

  // Measure the input on open + on layout shifts (drawer scroll, window
  // resize). `useLayoutEffect` so the popover positions before paint on
  // the first render after `open` flips true.
  useLayoutEffect(() => {
    if (!open) return;
    const rect = inputRef.current?.getBoundingClientRect() ?? null;
    setAnchorRect(rect);
    const reposition = () => {
      const r = inputRef.current?.getBoundingClientRect() ?? null;
      setAnchorRect(r);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true); // capture so drawer-body scroll fires it
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, matches.length]);

  // Outside-click closes. Rolling our own here (not the shared
  // useOutsideClick hook) because we need to treat clicks INSIDE the
  // portal-ed popover as "inside" — the popover isn't a DOM descendant
  // of the wrapper thanks to position:fixed.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (wrapperRef.current?.contains(target)) return;
      // Popover rows carry data-suggest-row; clicks on them dispatch
      // their own onMouseDown BEFORE this listener runs, so if we see
      // one here it's a legitimate outside click.
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const shouldShow = open && value.trim().length > 0;

  function fillFromSuggestion(s: Suggestion) {
    if (s.alreadyInProject) return;
    onChange(s.email);
    setOpen(false);
    // Return focus to the input so the user can tab to Occupation.
    inputRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!shouldShow) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Skip disabled rows.
      let next = activeIdx;
      for (let i = 0; i < matches.length; i++) {
        next = (next + 1) % matches.length;
        if (!matches[next].alreadyInProject) break;
      }
      setActiveIdx(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      let next = activeIdx;
      for (let i = 0; i < matches.length; i++) {
        next = (next - 1 + matches.length) % matches.length;
        if (!matches[next].alreadyInProject) break;
      }
      setActiveIdx(next);
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && matches[activeIdx] && !matches[activeIdx].alreadyInProject) {
        e.preventDefault();
        fillFromSuggestion(matches[activeIdx]);
      }
      // Else: Enter falls through to the surrounding form's default
      // submit (DrawerShell footer's Save button handler).
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <label
      ref={wrapperRef}
      style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}
    >
      <span className="label-up">
        {label}
        {required ? ' *' : ''}
      </span>
      <input
        ref={inputRef}
        className="inp"
        type="email"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shouldShow}
        aria-controls="email-suggest-list"
      />
      {shouldShow && anchorRect ? (
        <div
          id="email-suggest-list"
          role="listbox"
          style={{
            position: 'fixed',
            top: anchorRect.bottom + 4,
            left: anchorRect.left,
            width: anchorRect.width,
            maxHeight: 260,
            overflowY: 'auto',
            // `--panel` = pure white in light theme, near-black in dark
            // theme — matches the drawer's own surface color so the
            // dropdown reads as a natural extension of the input, not
            // as a darker floating card. Was `--bg-1` which appeared as
            // a tan "dark box" against the white light-mode drawer.
            background: 'var(--panel)',
            color: 'var(--text)',
            border: '1px solid var(--border-2)',
            borderRadius: 6,
            padding: 4,
            zIndex: 50,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}
        >
          {matches.length === 0 ? (
            <div
              style={{
                padding: '8px 10px',
                color: 'var(--muted)',
                fontSize: 12,
              }}
            >
              {workosRoster.isReady ? 'No matches.' : 'Loading roster…'}
            </div>
          ) : (
            matches.map((s, i) => (
              <SuggestionRow
                key={s.key}
                suggestion={s}
                active={i === activeIdx}
                onSelect={() => fillFromSuggestion(s)}
                onHover={() => {
                  if (!s.alreadyInProject) setActiveIdx(i);
                }}
              />
            ))
          )}
        </div>
      ) : null}
    </label>
  );
}

function SuggestionRow({
  suggestion: s,
  active,
  onSelect,
  onHover,
}: {
  suggestion: Suggestion;
  active: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  const disabled = s.alreadyInProject;
  const badge = badgeFor(s);
  return (
    <div
      role="option"
      aria-selected={active}
      aria-disabled={disabled}
      data-suggest-row
      // onMouseDown, not onClick — fires before the input's onBlur so
      // the click always lands even when focus is stealing attention.
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={onHover}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: active && !disabled ? 'var(--panel-2)' : 'transparent',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {s.displayName}
        </div>
        {s.displayName !== s.email ? (
          <div
            style={{
              fontSize: 11,
              color: 'var(--muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {s.email}
          </div>
        ) : null}
      </div>
      <SourceBadge tone={badge.tone} label={badge.label} />
    </div>
  );
}

type BadgeTone = 'good' | 'muted' | 'warn';

function badgeFor(s: Suggestion): { tone: BadgeTone; label: string } {
  if (s.alreadyInProject) return { tone: 'muted', label: 'Already in project' };
  if (s.source === 'bound') return { tone: 'good', label: 'In workforce' };
  if (s.source === 'workos-invite') {
    return { tone: 'warn', label: `Invite ${s.inviteState ?? 'pending'}` };
  }
  return { tone: 'muted', label: 'WorkOS' };
}

function SourceBadge({ tone, label }: { tone: BadgeTone; label: string }) {
  const palette: Record<BadgeTone, { bg: string; fg: string }> = {
    good: { bg: 'var(--good-soft)', fg: 'var(--good)' },
    warn: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
    muted: { bg: 'var(--panel-3)', fg: 'var(--muted)' },
  };
  const s = palette[tone];
  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 8px',
        borderRadius: 10,
        background: s.bg,
        color: s.fg,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
}
